import {
  Text,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  ButtonProps,
  Button,
} from "@chakra-ui/react";
import { ImmutableArray } from "@hookstate/core";
import { useMemo, forwardRef, useEffect } from "react";
import { usePopoverOpenState } from "../../utils/usePopoverOpenState";
import { RadioFilter } from "../filters/RadioFilter";
import { OrderIcon } from "../icons/OrderIcon";

export const DEFAULT_SORT_ORDER_TYPE = "popular";

interface SortOrderMenuProps {
  invalidate?: () => void;
  sortOrder?: string;
  onSortOrderChange: (value: string) => void;
  sortOrders?: ImmutableArray<{
    id: string;
    name: string;
  }>;
}

export function SortOrderMenu({
  invalidate,
  sortOrder,
  onSortOrderChange,
  sortOrders,
}: SortOrderMenuProps) {
  const { ref, onToggle, isOpen, onClose } = usePopoverOpenState();
  const value = sortOrder;

  const options = useMemo(
    () =>
      sortOrders
        ? sortOrders.map(({ id, name }) => {
            return {
              value: "" + id,
              label: name,
            };
          })
        : [],
    [sortOrders],
  );

  // Reset to default when relevance is removed from options (query cleared)
  useEffect(() => {
    const relevanceInOptions = options.some(
      (option) => option.label.toLowerCase() === "relevance",
    );
    if (!relevanceInOptions && value === "relevance") {
      onSortOrderChange(DEFAULT_SORT_ORDER_TYPE);
    }
  }, [sortOrders]);

  var activeOption = options.filter((option) => option.value == value);

  return (
    <Popover variant="filterSmall" gutter={4} isOpen={isOpen} isLazy>
      <PopoverTrigger>
        <SortOrderTrigger
          isActive={isOpen}
          style={{ pointerEvents: isOpen ? "none" : "auto" }}
          value={activeOption.length ? activeOption[0].label : "Popular"}
          onMouseUp={onToggle}
        />
      </PopoverTrigger>
      <PopoverContent
        ref={ref}
        variants={{
          enter: { opacity: 1, top: 0 },
          exit: { opacity: 0, top: -20 },
        }}
      >
        <PopoverBody>
          <RadioFilter
            tinted={false}
            value={value}
            options={options}
            onChange={(newOrder) => {
              invalidate?.();
              onSortOrderChange(newOrder);
              onClose();
            }}
          />
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
}

interface SortOrderTriggerProps extends ButtonProps {}
const SortOrderTrigger = forwardRef<HTMLButtonElement, SortOrderTriggerProps>(
  ({ ...props }, ref) => {
    return (
      <Button
        variant="sortMenu"
        size="md"
        height="36px"
        ref={ref}
        isActive={props.isActive}
        gap="8px"
        {...props}
      >
        <Text variant="filterTag">{props.value}</Text>
        <OrderIcon w={4.5} h={4.5} color="dune.100" />
      </Button>
    );
  },
);
